import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Euro, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, serviceCities, pricing } from '../data/mock';

const ZonesTarifs = () => {
  return (
    <div className="min-h-screen pt-24">
      {/* Header */}
      <section className="py-12 bg-gradient-to-br from-[#0B2A4A]/5 via-white to-[#2F80ED]/5">
        <div className="container mx-auto px-4">
          <Badge className="bg-[#2F80ED] text-white mb-4">Zones & Tarifs</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0B2A4A] mb-6">
            Zone d'intervention & tarifs
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
            Nous intervenons sur {companyInfo.zone} dans un rayon de {companyInfo.serviceRadius}. 
            Découvrez nos tarifs transparents et sans surprise.
          </p>
        </div>
      </section>

      {/* Zone Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#2F80ED] rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#0B2A4A]">
                  Zone d'intervention
                </h2>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                AquaTherm Plomberie intervient sur Lyon et toutes les communes du Grand Lyon 
                dans un rayon de {companyInfo.serviceRadius}. Pour toute demande hors zone, 
                contactez-nous pour étudier votre projet.
              </p>
              <div className="bg-[#2F80ED]/10 rounded-lg p-6 mb-6">
                <h3 className="font-bold text-[#0B2A4A] mb-3">Dépannage d'urgence 7j/7</h3>
                <p className="text-sm text-gray-700 mb-4">
                  Pour toute urgence (fuite, débouchage urgent, panne de chauffe-eau), 
                  nous intervenons 7 jours sur 7 dans notre zone d'intervention.
                </p>
                <a href={`tel:${companyInfo.phoneFormatted}`}>
                  <Button className="bg-[#2F80ED] hover:bg-[#0B2A4A] gap-2">
                    <Phone className="w-4 h-4" />
                    Appeler pour une urgence
                  </Button>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-[#0B2A4A] mb-6">
                Communes desservies
              </h3>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="grid grid-cols-2 gap-3">
                  {serviceCities.map((city, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-[#2F80ED] rounded-full"></div>
                      <span className="text-sm text-gray-700">{city}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mt-6 pt-6 border-t border-gray-200">
                  Et toutes les communes du Grand Lyon dans un rayon de {companyInfo.serviceRadius}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#2F80ED] rounded-lg flex items-center justify-center">
              <Euro className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-[#0B2A4A]">
              Tarifs indicatifs
            </h2>
          </div>
          
          <div className="bg-[#2F80ED]/10 rounded-lg p-6 mb-8 flex items-start gap-3">
            <Info className="w-5 h-5 text-[#2F80ED] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-700 font-semibold mb-2">
                Transparence des tarifs
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Les tarifs ci-dessous sont donnés à titre indicatif et peuvent varier selon 
                la complexité de l'intervention. Un devis clair et détaillé vous sera 
                toujours communiqué avant toute intervention.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pricing.map((item, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg text-[#0B2A4A] mb-2">
                    {item.service}
                  </h3>
                  <p className="text-2xl font-bold text-[#2F80ED] mb-3">
                    {item.price}
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {item.note}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <h3 className="text-xl font-bold text-[#0B2A4A] mb-4">
              Besoin d'un devis personnalisé ?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Chaque projet est unique. Contactez-nous pour obtenir un devis gratuit 
              et adapté à vos besoins.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-[#2F80ED] hover:bg-[#0B2A4A] transition-all duration-300">
                Demander un devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Payment Info */}
      <section className="py-12 bg-[#0B2A4A] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-6 text-center">
              Modalités de paiement
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#2F80ED] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💳</span>
                </div>
                <h4 className="font-semibold mb-2">Carte bancaire</h4>
                <p className="text-sm text-gray-300">Paiement par CB accepté</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#2F80ED] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💰</span>
                </div>
                <h4 className="font-semibold mb-2">Espèces</h4>
                <p className="text-sm text-gray-300">Paiement en espèces possible</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#2F80ED] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🏦</span>
                </div>
                <h4 className="font-semibold mb-2">Virement</h4>
                <p className="text-sm text-gray-300">Virement bancaire accepté</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ZonesTarifs;
